import { memo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowUpRight, Briefcase, Gauge } from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePersona, ALL_PERSONAS, type PersonaId } from '@/hooks/usePersona'
import { useChatStore } from '@/store/chatStore'

const PERSONA_ICONS: Record<PersonaId, typeof Briefcase> = {
  product_owner: Briefcase,
  operations: Gauge,
  architect: ArrowUpRight,
}

export default memo(function PersonaSwitcher({ compact = false }: { compact?: boolean }) {
  const { activePersona, switchPersona } = usePersona()
  const activeProjectId = useChatStore(s => s.activeProjectId)
  const navigate = useNavigate()
  const [switching, setSwitching] = useState(false)

  const completeSwitch = useCallback((persona: PersonaId) => {
    setSwitching(false)
    switchPersona(persona)
    navigate(`/p/${activeProjectId}`)
  }, [activeProjectId, navigate, switchPersona])

  const handleSwitch = async (persona: PersonaId) => {
    if (!activeProjectId || persona === activePersona || switching) return
    setSwitching(true)
    completeSwitch(persona)
  }

  if (!activeProjectId) return null

  return (
    <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-muted/50 border border-border">
      {ALL_PERSONAS.map(p => {
        const Icon = PERSONA_ICONS[p.id]
        const active = p.id === activePersona
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => handleSwitch(p.id)}
            title={p.description}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all duration-150',
              active
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Icon className="h-3 w-3 shrink-0" />
            {!compact && <span>{p.shortLabel}</span>}
          </button>
        )
      })}
      <button
        type="button"
        onClick={() => navigate(`/p/${activeProjectId}/architect`)}
        title="Open technical repository work in Forge"
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground transition-all duration-150 hover:text-foreground"
      >
        <ArrowUpRight className="h-3 w-3 shrink-0" />
        {!compact && <span>Forge</span>}
      </button>
    </div>
  )
})
